import { useEffect } from 'react';
import { Provider } from '@vgtv/api-client';
import classNames from 'classnames';

import { AccountSharing } from './components/AccountSharing/AccountSharing';
import { isHeaderless, isHermesApp, isIOS, isSportApp } from './utils/device';
import { Routes } from './Routes';
import { Footer } from './components/Footer/Footer';
import { Header } from './components/Header/Header';
import { getMenuItems } from './components/Header/helpers';
import { config } from './config';
import { ViewportType } from './utils/viewport/getViewportType';
import { useOverlay } from './components/Overlay/useOverlay';
import { SearchPopup } from './components/SearchPopup/SearchPopup';
import { DataController } from './components/DataController/DataController';
import { ContinueAsPopup } from './components/ContinueAsPopup/ContinueAsPopup';
import { AutologinPopup } from './components/AutologinPopup/AutologinPopup';
import { usePlayerBindings } from './components/utils/usePlayerBindings';
import { observeToolbarHide } from './utils/viewport/observeToolbarHide';
import { sessionStorageHelper } from './helpers/storage';
import { MobileNavigation } from './components/Header/MobileNavigation/MobileNavigation';
import { useAppDispatch } from './modules/hooks';
import { useWatchtime } from './components/utils/useWatchtime';
import { useCollections } from './components/utils/useCollections';
import { MQ_MOBILE_NAV, useMediaQuery } from './components/utils/useMediaQuery';
import { ParentalControls } from './components/ParentalControl/ParentalControls';
import { useErrorRecovery } from './components/Player/useErrorRecovery';
import { FavouritesPopup } from './components/FavouritesPopup/FavouritesPopup';
import { useFavouritesPopup } from './components/FavouritesPopup/useFavouritesPopup';
import { getTracker } from './services/tracking/pulse/pulse';

import * as styles from './App.module.scss';

const mobileMenuItems = getMenuItems(config, ViewportType.MOBILE);

function App() {
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(MQ_MOBILE_NAV);
  const { isOpen, open, close } = useOverlay();

  const { favouritesPopupVisible } = useFavouritesPopup();

  useEffect(() => {
    const stopObserving = observeToolbarHide();

    return () => {
      if (typeof stopObserving === 'function') {
        stopObserving();
      }
    };
  }, []);

  useErrorRecovery();
  usePlayerBindings();

  useWatchtime();
  useCollections();

  useEffect(() => {
    getTracker();

    document.body.classList.add(`provider-${config.provider}`);

    if (isHeaderless) {
      document.body.classList.add(`is-headerless`);
    }
    if (isHermesApp) {
      document.body.classList.add(`is-hermes`);
    }

    if (isIOS) {
      document.body.classList.add(`is-ios`);
    }
    const initialUrl = window.location.href;

    sessionStorageHelper.setItem(
      'isPwa',
      /[?&]utm_source=pwa(&|$)/.test(initialUrl)
    );
  }, [dispatch]);

  const getFavouritesPopup = () => {
    if (
      favouritesPopupVisible &&
      config.provider === Provider.VGTV &&
      !isSportApp
    ) {
      return <FavouritesPopup />;
    }

    return null;
  };

  return (
    <div className={styles.app}>
      <div
        className={classNames(styles.appContent, {
          [styles.withHeader]: !isHeaderless,
        })}
      >
        {!isHeaderless && <Header onSearchClick={open} />}
        <Routes />
      </div>
      <Footer />
      <DataController />
      {isMobile && (config.provider === Provider.AB || !isSportApp) && (
        <MobileNavigation
          mainMenuItems={mobileMenuItems.mainMenuItems}
          subMenuItems={mobileMenuItems.subMenuItems}
        />
      )}
      <SearchPopup isOpen={isOpen} close={close} />
      {config.asd.enabled && <AccountSharing />}
      <ParentalControls />
      {getFavouritesPopup()}
      {config.provider === Provider.VGTV ? (
        <AutologinPopup />
      ) : (
        <ContinueAsPopup />
      )}
    </div>
  );
}

export default App;
